import type { ExternalTableSchema } from '@packages/table-builder';

export const brokerageAccountListTable: ExternalTableSchema = {
  metadata: {
    title: 'Brokerage Account',
    entity: 'brokerage-account',
  },
  dataSource: {
    endpoint: '/api/customer/api/app/participant-broker-account/participant-list',
    method: 'GET',
    rowIdField: 'participantId',
    responseMapping: { items: 'items', totalCount: 'totalCount' },
    staticQueryParams: { IsOnboarded: true },
    saveActionId: 'SAVE_BROKERAGE_ACCOUNT',
    saveSuccessMessage: 'Brokerage Account updated successfully.',
    onSaveSuccess: 'toastAndRefetch',
    requestParams: {
      Filter: 'table.searchText',
      Sorting: 'table.sorting',
      SkipCount: 'table.pageOffset',
      MaxResultCount: 'table.pageSize',
      CompanyIds: 'filters.companyIds',
      BrokerStatusIds: 'filters.brokerStatusIds',
      BrokerIds: 'filters.brokerIds',
    },
    filters: [
      {
        name: 'companyIds',
        placeholder: 'Company',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/participant-broker-account/company-list',
          labelField: 'companyName',
          valueField: 'id',
        },
      },
      {
        name: 'brokerStatusIds',
        placeholder: 'Broker Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/participant-broker-account/broker-status-list',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'brokerIds',
        placeholder: 'Broker',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/broker/options',
          labelField: 'name',
          valueField: 'companyId',
        },
      },
    ],
  },
  columns: [
    { field: 'creationTime', header: 'Requested Date', type: 'date', width: 180, sortable: true },
    { field: 'companyName', header: 'Company Name', type: 'text', width: 250, sortable: true },
    { field: 'employeeId', header: 'Employee Id', type: 'text', width: 150, sortable: true },
    { field: 'name', header: 'Full Name', type: 'text', width: 200, sortable: true },
    { field: 'personalPhone', header: 'Mobile Number', type: 'text', width: 180 },
    { field: 'personalEmail', header: 'Email Address', type: 'text', width: 250 },
    {
      field: 'dateOfBirth',
      header: 'Date Of Birth',
      type: 'sensitiveReveal',
      width: 180,
      sortable: true,
      align: 'center',
      sensitiveReveal: {
        maskFormat: 'full',
        maskType: 'DOB',
        idField: 'participantId',
        endpoint: '/api/customer/api/app/participant-broker-account/participant-sensitive-data?field=DOB',
        method: 'GET',
        paramName: 'participantId',
        responseField: 'dateOfBirth',
        dateFormat: 'MM/DD/YYYY',
        presenceField: 'hasDateOfBirth',
        placeholder: 'xx/xx/xxxx',
        extraRowFields: { companyId: 'companyId' },
      },
    },
    {
      field: 'taxId_SSN',
      header: 'SSN/Tax Id',
      type: 'sensitiveReveal',
      width: 180,
      sortable: true,
      align: 'center',
      sensitiveReveal: {
        maskType: 'SSN',
        maskFormat: 'partial',
        idField: 'participantId',
        endpoint: '/api/customer/api/app/participant-broker-account/participant-sensitive-data?field=SSN',
        method: 'GET',
        paramName: 'participantId',
        responseField: 'taxId_SSN',
        presenceField: 'participantId',
        placeholder: 'xxx-xx-xxxx',
        extraRowFields: { companyId: 'companyId' },
      },
    },
    { field: 'address', header: 'Address', type: 'text', width: 300 },
    {
      field: 'isSection16',
      header: 'Is Section 16',
      type: 'badge',
      width: 150,
      align: 'center',
      badge: { statusMap: { Yes: 'success', No: 'default' } },
      conditionalValues: { true: 'Yes', false: 'No' },
    },
    {
      field: 'isRule144',
      header: 'Is Rule 144',
      type: 'badge',
      width: 150,
      align: 'center',
      badge: { statusMap: { Yes: 'success', No: 'default' } },
      conditionalValues: { true: 'Yes', false: 'No' },
    },
    {
      field: 'brokerName',
      header: 'Broker',
      type: 'text',
      width: 180,
      editable: {
        type: 'select',
        rowOptionsField: 'brokers',
        rowOptionsLabelField: 'brokerCompanyName',
        rowOptionsValueField: 'brokerCompanyId',
        defaultValueField: 'brokerId',
        effectToFields: [{ field: 'brokerId', fromOptionField: 'brokerCompanyId' }],
      },
    },
    { field: 'accountNumber', header: 'Account Number', type: 'text', width: 200, editable: true },
    { field: 'accountNumberCreatedDate', header: 'Account Number Created Date', type: 'date', width: 250 },
    { field: 'accountNumberUpdatedDate', header: 'Account Number Updated Date', type: 'date', width: 250 },
    {
      field: 'error',
      header: 'Error',
      type: 'text',
      width: 150,
      editable: true,
      conditionalValues: { true: 'Error', false: 'No Error' },
    },
    {
      field: 'brokerStatus',
      header: 'Broker Status',
      type: 'badge',
      width: 180,
      editable: { type: 'asyncSelect', placeholder: 'Select status' },
      badge: {
        statusMap: { Active: 'success', Inactive: 'warning', Suspended: 'error' },
      },
      dataSource: {
        url: '/api/customer/api/app/participant-broker-account/broker-status-list',
        mapper: { label: 'displayName', value: 'displayName' },
      },
    },
  ],
  actions: {
    row: [
      { id: 'EDIT_ROW_BROKERAGE_ACCOUNT', label: 'Edit', actionType: 'startEditing' },
      { id: 'VIEW_HISTORY_BROKERAGE_ACCOUNT', label: 'View History' },
      { id: 'VIEW_TAX_CERTIFICATES_BROKERAGE_ACCOUNT', label: 'View Tax Certificates' },
      { id: 'VIEW_GOVERNMENT_DOCUMENTS_BROKERAGE_ACCOUNT', label: 'View Government Documents' },
    ],
  },
  features: {
    search: { enabled: true, placeholder: 'Search' },
    pagination: { mode: 'infiniteScroll', defaultSize: 10 },
    editing: { bulkSave: true },
    columnPreferences: { persistenceKey: 'Brokerage Account', resizable: true, reorderable: true },
  },
};
